/* ============================================================
   IAY Glow — global.js
   Vanilla replacements for the source theme's jQuery stack:
   - Carousel (scroll-snap track + arrows/dots/autoplay)
   - Drawers (mobile menu) + overlay + scroll lock
   - Search canvas
   - Scroll reveals (IntersectionObserver, replaces WOW.js)
   - Image "settle" on load (replaces lazysizes visual)
   ============================================================ */

(function () {
  'use strict';

  /* ---- Scroll reveal ---- */
  /* Content must NEVER stay invisible: skip the animation entirely on
     mobile (throttled renderers can starve IntersectionObserver), and
     run a fail-safe that force-reveals everything after 1.5s. */
  function initReveals() {
    var els = document.querySelectorAll('.reveal');
    function showAll() {
      els.forEach(function (el) { el.classList.add('is-visible'); });
    }
    var isMobile = window.matchMedia('(max-width: 991px)').matches;
    if (isMobile || !('IntersectionObserver' in window)) {
      showAll();
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach(function (el) { io.observe(el); });
    setTimeout(function () {
      var pending = document.querySelectorAll('.reveal:not(.is-visible)');
      if (pending.length === els.length) {
        // Observer never fired at all — renderer is starved; reveal everything.
        showAll();
        io.disconnect();
      }
    }, 1500);
  }

  /* ---- Image settle ---- */
  function initImageSettle() {
    document.querySelectorAll('img.img-settle').forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', function () { img.classList.add('is-loaded'); }, { once: true });
        img.addEventListener('error', function () { img.classList.add('is-loaded'); }, { once: true });
      }
    });
  }

  /* ---- Carousel ---- */
  function initCarousel(root) {
    var track = root.querySelector('.carousel__track');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.children);
    if (!slides.length) return;

    var dotsWrap = root.querySelector('[data-carousel-dots]');
    // Arrows may live outside the .carousel (e.g. featured-collection header row)
    var scope = root.closest('.shopify-section') || document;
    var prevBtn = scope.querySelector('[data-carousel-prev]');
    var nextBtn = scope.querySelector('[data-carousel-next]');
    var dots = [];

    function perView() {
      var v = getComputedStyle(root).getPropertyValue('--per-view');
      return Math.max(1, parseInt(v, 10) || 1);
    }
    function pageCount() { return Math.max(1, Math.ceil(slides.length / perView())); }
    function currentPage() {
      var slideW = slides[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap || 0);
      if (!slideW) return 0;
      return Math.round(track.scrollLeft / (slideW * perView()));
    }
    function goTo(page) {
      var slideW = slides[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap || 0);
      track.scrollTo({ left: page * slideW * perView(), behavior: 'smooth' });
    }

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      dots = [];
      var n = pageCount();
      if (n < 2) return;
      for (var i = 0; i < n; i++) {
        var b = document.createElement('button');
        b.className = 'carousel__dot';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        (function (idx) { b.addEventListener('click', function () { goTo(idx); }); })(i);
        dotsWrap.appendChild(b);
        dots.push(b);
      }
      syncDots();
    }
    function syncDots() {
      var cur = currentPage();

      // Ken Burns hook for hero slides
      slides.forEach(function (s, i) {
        s.classList.toggle('is-current', Math.floor(i / perView()) === cur);
      });

      if (!dots.length) return;
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === cur); });

      // Dots are light by default (they sit over photography). When the current
      // slide is a light field with ink copy, white-on-cream is invisible —
      // flip them to ink so the indicator is always legible.
      if (dotsWrap) {
        var active = slides[cur * perView()];
        var onLight = !!(active && active.querySelector('.hero__caption--dark'));
        dotsWrap.classList.toggle('is-on-light', onLight);
      }
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(Math.max(0, currentPage() - 1)); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(Math.min(pageCount() - 1, currentPage() + 1)); });

    var scrollTimer;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(syncDots, 80);
    }, { passive: true });
    window.addEventListener('resize', buildDots);

    /* Autoplay (hero). Pauses on hover and resumes on leave — an earlier version
       killed the timer permanently on first hover, so the hero stopped advancing. */
    var autoplaySec = parseFloat(root.dataset.autoplay || 0);
    if (autoplaySec > 0 && slides.length > 1) {
      var timer = null;

      function advance() {
        if (document.hidden) return;
        goTo((currentPage() + 1) % pageCount());
      }
      function play() {
        if (timer) return;
        timer = setInterval(advance, autoplaySec * 1000);
      }
      function pause() {
        clearInterval(timer);
        timer = null;
      }

      root.addEventListener('pointerenter', pause);
      root.addEventListener('pointerleave', play);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', play);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) { pause(); } else { play(); }
      });
      play();
    }

    buildDots();
    syncDots();

    // Slide widths depend on images having laid out. DOMContentLoaded fires
    // before that, so re-measure once everything has loaded or the indicator
    // can build against a zero-width slide and show nothing.
    window.addEventListener('load', function () {
      buildDots();
      syncDots();
    });
  }

  /* ---- Drawers + overlay ---- */
  function initDrawers() {
    var overlay = document.querySelector('[data-page-overlay]');
    var openDrawer = null;

    function close() {
      if (!openDrawer) return;
      openDrawer.classList.remove('is-open');
      openDrawer.setAttribute('aria-hidden', 'true');
      openDrawer = null;
      if (overlay) overlay.classList.remove('is-active');
      document.body.classList.remove('drawer-open');
    }

    document.querySelectorAll('[data-drawer-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = document.querySelector('[data-drawer="' + btn.dataset.drawerOpen + '"]');
        if (!target) return;
        target.classList.add('is-open');
        target.setAttribute('aria-hidden', 'false');
        openDrawer = target;
        if (overlay) overlay.classList.add('is-active');
        document.body.classList.add('drawer-open');
      });
    });

    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(); closeSearch(); }
    });

    /* Search canvas */
    var searchCanvas = document.querySelector('[data-search-canvas]');
    function closeSearch() {
      if (searchCanvas) searchCanvas.classList.remove('is-open');
    }
    document.querySelectorAll('[data-toggle-search]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!searchCanvas) return;
        var opening = !searchCanvas.classList.contains('is-open');
        searchCanvas.classList.toggle('is-open');
        if (opening) {
          var input = searchCanvas.querySelector('input[type="search"]');
          if (input) setTimeout(function () { input.focus(); }, 350);
        }
      });
    });
    document.addEventListener('click', function (e) {
      if (searchCanvas && searchCanvas.classList.contains('is-open') &&
          !searchCanvas.contains(e.target) && !e.target.closest('[data-toggle-search]')) {
        closeSearch();
      }
    });
  }

  /* ---- Quick add (AJAX add to cart) ---- */
  function initQuickAdd() {
    // Surface an add failure inline (e.g. "sold out") instead of redirecting to
    // /cart/add, which renders Shopify's raw "parameter" error page.
    function showQuickAddError(form, msg) {
      var host = form.closest('product-quick-add') || form.parentElement;
      var el = host.querySelector('.quick-add-error');
      if (!el) {
        el = document.createElement('div'); el.className = 'quick-add-error';
        el.style.cssText = 'margin-top:8px;font-size:1.25rem;line-height:1.35;color:#b00020;';
        host.appendChild(el);
      }
      el.textContent = msg;
      clearTimeout(el._t); el._t = setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 5000);
    }
    document.querySelectorAll('product-quick-add form').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('button[type="submit"]');
        var label = btn.textContent;
        btn.textContent = 'Adding…';
        fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(new FormData(form))
        })
          .then(function (r) {
            return r.json().catch(function () { return {}; }).then(function (data) {
              // Shopify returns 422 + { description } when an item can't be added (e.g. out of stock).
              if (!r.ok) { throw new Error((data && (data.description || data.message)) || 'This item could not be added to your cart.'); }
              return data;
            });
          })
          .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
          .then(function (cart) {
            document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
            btn.textContent = 'Added ✓';
            setTimeout(function () { btn.textContent = label; }, 1500);
            if (window.iayCartDrawer) window.iayCartDrawer.renderAndOpen(cart);
          })
          .catch(function (err) {
            btn.textContent = label;
            showQuickAddError(form, (err && err.message) || 'Could not add to cart. Please try again.');
          });
      });
    });
  }

  /* ---- Cart drawer (Cymbiotika-pattern slide-in) ----
     Liquid renders first paint in sections/cart-drawer.liquid; everything
     after that is re-rendered here from /cart.js JSON so the two templates
     MUST stay in sync (same class names). Qty changes go through
     /cart/change.js addressed by 1-based line number. */
  function initCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    var body = drawer.querySelector('[data-cart-drawer-body]');
    var foot = drawer.querySelector('[data-cart-drawer-foot]');
    var busy = false;

    function money(cents) { return '$' + (cents / 100).toFixed(2); }
    function esc(s) {
      return String(s).replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
    }

    function open() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-drawer-open');
      var closeBtn = drawer.querySelector('.cart-drawer__close');
      if (closeBtn) closeBtn.focus();
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-drawer-open');
    }

    function itemRow(item, line) {
      var variant = item.variant_title && item.variant_title !== 'Default Title'
        ? '<div class="cart-drawer__item-variant">' + esc(item.variant_title) + '</div>' : '';
      var sub = item.selling_plan_allocation
        ? '<div class="cart-drawer__item-sub"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" width="12" height="12" aria-hidden="true"><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5"/></svg>' + esc(item.selling_plan_allocation.selling_plan.name) + '</div>' : '';
      var img = item.image
        ? '<img src="' + esc(item.image) + '" alt="' + esc(item.product_title) + '" loading="lazy">' : '';
      var compare = item.original_line_price > item.final_line_price
        ? '<s class="cart-drawer__item-compare">' + money(item.original_line_price) + '</s>' : '';
      return '<div class="cart-drawer__item" data-line="' + line + '" data-product-id="' + item.product_id + '">' +
        '<a href="' + esc(item.url) + '" class="cart-drawer__item-media">' + img + '</a>' +
        '<div class="cart-drawer__item-info">' +
          '<div class="cart-drawer__item-top">' +
            '<a href="' + esc(item.url) + '" class="cart-drawer__item-title">' + esc(item.product_title) + '</a>' +
            '<button type="button" class="cart-drawer__item-remove" data-remove-item aria-label="Remove">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="15" height="15"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
            '</button>' +
          '</div>' + variant + sub +
          '<div class="cart-drawer__item-price">' + money(item.final_line_price) + compare + '</div>' +
          '<div class="cart-drawer__qty">' +
            '<button type="button" data-qty-minus aria-label="Decrease quantity">&minus;</button>' +
            '<span class="cart-drawer__qty-num">' + item.quantity + '</span>' +
            '<button type="button" data-qty-plus aria-label="Increase quantity">+</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    }

    /* Persistent chrome (promo banner, upsell) is rendered once by Liquid and
       lives OUTSIDE the re-rendered items container so it survives updates. */
    var itemsWrap = drawer.querySelector('[data-cart-drawer-items]');
    var emptyEl = drawer.querySelector('[data-cart-drawer-empty]');
    var savingsEl = drawer.querySelector('[data-cart-drawer-savings]');
    var upsell = drawer.querySelector('[data-cart-upsell]');
    var promo = drawer.querySelector('.cart-drawer__promo');

    function setText(sel, val) { var el = foot && foot.querySelector(sel); if (el) el.textContent = val; }

    function render(cart) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
      var countEl = drawer.querySelector('[data-cart-drawer-count]');
      if (countEl) countEl.textContent = cart.item_count;

      if (cart.item_count === 0) {
        // collapse the rich chrome, show the empty state
        if (emptyEl) emptyEl.hidden = false;
        if (itemsWrap) itemsWrap.innerHTML = '';
        if (savingsEl) savingsEl.hidden = true;
        // promo banner stays pinned + visible even on an empty cart
        if (upsell) upsell.hidden = true;
        if (foot) foot.hidden = true;
        return;
      }

      if (emptyEl) emptyEl.hidden = true;
      if (itemsWrap) itemsWrap.innerHTML = cart.items.map(function (item, i) { return itemRow(item, i + 1); }).join('');
      if (promo) promo.hidden = false;

      // Savings banner — only when the order actually carries a discount
      if (savingsEl) {
        if (cart.total_discount > 0) { savingsEl.hidden = false; savingsEl.textContent = money(cart.total_discount) + ' in savings unlocked with your order!'; }
        else savingsEl.hidden = true;
      }

      // Order summary
      if (foot) {
        foot.hidden = false;
        setText('[data-cart-drawer-subtotal]', money(cart.items_subtotal_price));
        setText('[data-cart-drawer-total]', money(cart.total_price));
        var discRow = foot.querySelector('[data-cart-drawer-discount-row]');
        if (discRow) {
          if (cart.total_discount > 0) { discRow.hidden = false; setText('[data-cart-drawer-discount]', '-' + money(cart.total_discount)); }
          else discRow.hidden = true;
        }
      }

      // Hide the cross-sell once its product is already in the cart
      if (upsell) {
        var upId = parseInt(upsell.dataset.upsellProductId, 10);
        upsell.hidden = cart.items.some(function (item) { return item.product_id === upId; });
      }
    }

    function refresh(thenOpen) {
      fetch('/cart.js')
        .then(function (r) { if (!r.ok) throw new Error('cart fetch failed'); return r.json(); })
        .then(function (cart) { render(cart); if (thenOpen) open(); })
        .catch(function () { if (thenOpen) open(); }); // offline/preview: show server-rendered state
    }

    function changeLine(line, quantity) {
      if (busy) return;
      busy = true;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
        .then(render)
        .catch(function () {})
        .then(function () { busy = false; });
    }

    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-drawer-close]')) { close(); return; }
      var row = e.target.closest('.cart-drawer__item');
      if (row) {
        var line = parseInt(row.dataset.line, 10);
        var qty = parseInt(row.querySelector('.cart-drawer__qty-num').textContent, 10);
        if (e.target.closest('[data-qty-plus]')) changeLine(line, qty + 1);
        else if (e.target.closest('[data-qty-minus]')) changeLine(line, qty - 1);
        else if (e.target.closest('[data-remove-item]')) changeLine(line, 0);
        return;
      }
      var add = e.target.closest('[data-upsell-add]');
      if (add) {
        if (busy) return;
        busy = true;
        add.textContent = '…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(add.dataset.variantId, 10), quantity: 1 })
        })
          .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
          .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
          .then(render)
          .catch(function () {})
          .then(function () { busy = false; add.textContent = 'Add'; });
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Header cart icon opens the drawer instead of navigating to /cart
    document.querySelectorAll('.header-icon--cart').forEach(function (icon) {
      icon.addEventListener('click', function (e) {
        e.preventDefault();
        refresh(true);
      });
    });

    window.iayCartDrawer = {
      open: open,
      close: close,
      refresh: refresh,
      renderAndOpen: function (cart) { render(cart); open(); }
    };
  }

  /* ---- PDP buy-box info tabs (Benefits / Testing / Description / ...) ---- */
  function initPdpTabs() {
    var tabs = document.querySelectorAll('[data-pdp-tab]');
    if (!tabs.length) return;
    var panels = document.querySelectorAll('[data-pdp-tab-panel]');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var i = tab.dataset.pdpTab;
        tabs.forEach(function (t) { t.classList.toggle('is-active', t.dataset.pdpTab === i); });
        panels.forEach(function (p) { p.classList.toggle('is-active', p.dataset.pdpTabPanel === i); });
      });
    });
  }

  /* ---- PDP ingredients: circle strip content switcher (Figma 186:2971) ---- */
  function initPdpIngredients() {
    document.querySelectorAll('[data-pdp-ing]').forEach(function (root) {
      var dots = root.querySelectorAll('[data-pdp-ing-dot]');
      if (dots.length < 2) return;
      var names = root.querySelectorAll('[data-pdp-ing-name]');
      var panels = root.querySelectorAll('[data-pdp-ing-panel]');
      var count = dots.length, current = 0;
      function show(i) {
        current = (i + count) % count;
        dots.forEach(function (d, n) { d.classList.toggle('is-active', n === current); });
        names.forEach(function (el) { el.classList.toggle('is-active', parseInt(el.dataset.pdpIngName, 10) === current); });
        panels.forEach(function (el) { el.classList.toggle('is-active', parseInt(el.dataset.pdpIngPanel, 10) === current); });
      }
      dots.forEach(function (dot, i) { dot.addEventListener('click', function () { show(i); }); });
      var prev = root.querySelector('[data-pdp-ing-prev]');
      var next = root.querySelector('[data-pdp-ing-next]');
      if (prev) prev.addEventListener('click', function () { show(current - 1); });
      if (next) next.addEventListener('click', function () { show(current + 1); });
    });
  }

  /* ---- Ingredient science: fixed-panel content switcher ----
     The dark section + header stay put; only the active tab (card + detail
     + botanical image) is shown, the others overlaid and hidden. Dots
     crossfade between ingredients. NOT a sliding carousel. */
  function initIngredientTabs() {
    document.querySelectorAll('[data-ing-tabs]').forEach(function (root) {
      var tabs = root.querySelectorAll('[data-ing-tab]');
      var dots = root.querySelectorAll('[data-ing-dot]');
      var prev = root.querySelector('[data-ing-prev]');
      var next = root.querySelector('[data-ing-next]');
      if (dots.length < 2) return;
      var count = dots.length;
      var current = 0;
      /* Card tab (left) and detail tab (right) share the same data-ing-tab
         index — match on the attribute, not DOM position, so both swap. */
      function show(i) {
        current = (i + count) % count;   /* wrap around for the arrows */
        tabs.forEach(function (t) { t.classList.toggle('is-active', parseInt(t.dataset.ingTab, 10) === current); });
        dots.forEach(function (d, n) { d.classList.toggle('is-active', n === current); });
      }
      /* Autoplay — advance every N seconds (data-ing-autoplay). Pauses on hover
         so people can read, and any manual click restarts the clock. */
      var delay = parseInt(root.dataset.ingAutoplay, 10) || 0;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var timer = null;
      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      function play() { if (delay <= 0 || reduce) return; stop(); timer = setInterval(function () { show(current + 1); }, delay * 1000); }
      function go(i) { show(i); play(); }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { go(i); });
      });
      if (prev) prev.addEventListener('click', function () { go(current - 1); });
      if (next) next.addEventListener('click', function () { go(current + 1); });
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', play);
      play();
    });
  }

  /* ---- PDP: thumbnail strip <-> gallery sync ---- */
  function initProductGallery() {
    var gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;
    var track = gallery.querySelector('[data-gallery-track]');
    var thumbs = document.querySelectorAll('[data-thumb-index]');
    if (!track || !thumbs.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var i = parseInt(thumb.dataset.thumbIndex, 10);
        var slide = track.children[i];
        if (slide) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });

    var timer;
    track.addEventListener('scroll', function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        var w = track.children[0] ? track.children[0].getBoundingClientRect().width : 0;
        if (!w) return;
        var idx = Math.round(track.scrollLeft / w);
        thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === idx); });
      }, 80);
    }, { passive: true });
  }

  /* ---- PDP: quantity stepper ---- */
  function initQuantity() {
    document.querySelectorAll('.quick_view_qty').forEach(function (wrap) {
      var input = wrap.querySelector('.quantity-selector');
      if (!input) return;
      wrap.querySelectorAll('.quick_view-qty').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          // The steppers are anchors (source-theme markup) — without this the
          // '#' navigation scrolls the page and the click feels dead.
          e.preventDefault();
          var v = parseInt(input.value, 10) || 1;
          v = btn.classList.contains('quick_view-qty-plus') ? v + 1 : Math.max(1, v - 1);
          input.value = v;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ---- PDP: purchase plan toggle (Subscribe & Save / One-Time) ----
     Wired to a real Shopify selling plan: each box carries its plan id in
     data-selling-plan (Subscribe = the plan id, One-Time = ""). On select we
     copy that id into the hidden `selling_plan` input the product form posts,
     so /cart/add.js attaches the subscription (empty = one-time). The unselected
     box dims via CSS (:not(.is-selected)). */
  function initPdpPlans() {
    var plans = document.querySelectorAll('[data-plan-option]');
    if (!plans.length) return;
    var hidden = document.querySelector('input[name="selling_plan"]');
    function applyPlan(plan) {
      if (hidden) hidden.value = plan.getAttribute('data-selling-plan') || '';
    }
    plans.forEach(function (plan) {
      plan.addEventListener('click', function () {
        plans.forEach(function (p) {
          p.classList.remove('is-selected');
          var r = p.querySelector('.pdp__radio');
          if (r) r.classList.remove('is-on');
        });
        plan.classList.add('is-selected');
        var radio = plan.querySelector('.pdp__radio');
        if (radio) radio.classList.add('is-on');
        applyPlan(plan);
      });
    });
    // Sync the hidden input to whichever box is pre-selected on load (Subscribe).
    var initial = document.querySelector('[data-plan-option].is-selected');
    if (initial) applyPlan(initial);
  }

  /* ---- PDP: variant selection ----
     Matches the selected radio values against the variant JSON, then updates
     the hidden id, price, SKU, availability and the add-to-cart label. */
  function initVariants() {
    var host = document.querySelector('product-variants');
    if (!host) return;
    var json = host.querySelector('[data-variant-json]');
    if (!json) return;
    var variants;
    try { variants = JSON.parse(json.textContent); } catch (e) { return; }

    var idField = document.querySelector('[data-variant-id]');
    var priceEl = document.querySelector('.product-price__price');
    var skuEl = document.querySelector('[data-variant-sku]');
    var availEl = document.querySelector('.product__available');
    var atcWrap = document.querySelector('.product-form__item--submit');
    var atcBtn = document.getElementById('AddToCart');
    var atcText = document.getElementById('AddToCartText');

    function money(cents) {
      var fmt = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency', currency: fmt
        }).format(cents / 100);
      } catch (e) { return '$' + (cents / 100).toFixed(2); }
    }

    function selected() {
      return Array.prototype.map.call(
        host.querySelectorAll('input[type="radio"]:checked'),
        function (i) { return i.value; }
      );
    }

    function update() {
      var chosen = selected();
      var match = variants.find(function (v) {
        return chosen.every(function (val, i) { return v.options[i] === val; });
      });

      host.querySelectorAll('fieldset').forEach(function (fs) {
        var cur = fs.querySelector('.variant_current');
        var checked = fs.querySelector('input[type="radio"]:checked');
        if (cur && checked) cur.textContent = checked.value;
      });

      if (!match) {
        if (atcText) atcText.textContent = 'Unavailable';
        if (atcWrap) atcWrap.classList.add('soldout');
        return;
      }

      if (idField) idField.value = match.id;
      if (priceEl) priceEl.textContent = money(match.price);
      if (skuEl) skuEl.textContent = match.sku || '';
      if (availEl) {
        availEl.textContent = match.available ? 'In stock' : 'Out of stock';
        availEl.classList.toggle('in-stock', match.available);
        availEl.classList.toggle('out-stock', !match.available);
      }
      if (atcText) atcText.textContent = match.available ? 'Add to bag' : 'Sold out';
      if (atcBtn) atcBtn.disabled = !match.available;
      if (atcWrap) atcWrap.classList.toggle('soldout', !match.available);

      if (window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      }
    }

    host.addEventListener('change', function (e) {
      if (e.target.type === 'radio') update();
    });
    update();
  }

  /* ---- Tabs ---- */
  function initTabs() {
    document.querySelectorAll('[data-tab-link]').forEach(function (link) {
      link.addEventListener('click', function () {
        var id = link.dataset.tabLink;
        var scope = link.closest('.product-single__tab') || document;
        scope.querySelectorAll('[data-tab-link]').forEach(function (l) { l.classList.remove('active'); });
        scope.querySelectorAll('.tab-pane').forEach(function (p) { p.classList.remove('active'); });
        link.classList.add('active');
        var pane = scope.querySelector('#' + id);
        if (pane) pane.classList.add('active');
      });
    });
  }

  /* ---- Modals (share) ---- */
  function initModals() {
    document.querySelectorAll('[data-open-share]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var m = document.querySelector('[data-modal="share"]');
        if (m) { m.classList.add('is-open'); m.setAttribute('aria-hidden', 'false'); }
      });
    });
    document.querySelectorAll('.nov-modal').forEach(function (modal) {
      modal.addEventListener('click', function (e) {
        if (e.target === modal || e.target.closest('[data-modal-close]')) {
          modal.classList.remove('is-open');
          modal.setAttribute('aria-hidden', 'true');
        }
      });
    });
    document.querySelectorAll('[data-copy-link]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = btn.parentElement.querySelector('input');
        if (!input) return;
        input.select();
        try { document.execCommand('copy'); } catch (e) {}
        var t = btn.textContent;
        btn.textContent = 'Copied';
        setTimeout(function () { btn.textContent = t; }, 1500);
      });
    });
  }

  /* ---- FAQ accordion ----
     Shared by the FAQ section (.faq__item) AND the PDP why-matters
     accordion (.why-matters__item) — both wire [data-faq-toggle], so the
     closest() lookup must know both item classes or one silently dies. */
  function initFaq() {
    document.querySelectorAll('[data-faq-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('.faq__item, .why-matters__item');
        if (!item) return;
        // Why-matters is an EXCLUSIVE accordion (Aaron 2026-07-24): opening
        // one item closes its siblings, and the open item cannot be closed —
        // exactly one stays open at all times. Page FAQs stay independent.
        if (item.classList.contains('why-matters__item')) {
          if (item.classList.contains('is-open')) return;
          item.parentElement.querySelectorAll('.why-matters__item.is-open').forEach(function (sib) {
            sib.classList.remove('is-open');
            var b = sib.querySelector('[data-faq-toggle]');
            if (b) b.setAttribute('aria-expanded', 'false');
          });
        }
        var open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ---- PDP why-matters: media height EXACTLY matches the accordion ----
     Measured at load (first item open, the default state) and held there —
     toggling items must not resize the photo (Aaron). Recomputed on resize.
     Skipped when the layout is stacked (single column, <=991px). */
  function initWhyMattersHeight() {
    var media = document.querySelector('.why-matters__media');
    var list = document.querySelector('.why-matters__list');
    if (!media || !list) return;
    function size() {
      if (window.innerWidth <= 991) { media.style.height = ''; return; }
      media.style.height = '';
      media.style.height = list.offsetHeight + 'px';
    }
    size();
    window.addEventListener('load', size);
    var t;
    window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(size, 150); });
  }

  /* ---- Entry signup modal (signup-modal section) ----
     Opens after data-delay seconds on first visit; a dismissal is remembered
     in localStorage for data-dismiss-days. `?signup=show` forces it open
     (review/testing). Esc, the X, and successful signup all close it. */
  function initSignupModal() {
    var modal = document.querySelector('[data-signup-modal]');
    if (!modal) return;
    var KEY = 'iay_signup_until';
    var days = parseInt(modal.getAttribute('data-dismiss-days') || '7', 10);
    function remember() {
      try { localStorage.setItem(KEY, String(Date.now() + days * 86400000)); } catch (e) {}
    }
    function open() {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('signup-modal-open');
    }
    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('signup-modal-open');
      remember();
    }
    // Close/open bindings ALWAYS attach — the sticky tab must reopen the
    // modal even when the auto-show is suppressed by a stored dismissal.
    document.querySelectorAll('[data-signup-open]').forEach(function (b) { b.addEventListener('click', open); });
    // Sticky tab has its own X: hides the tab and remembers for dismiss_days.
    var TAB_KEY = 'iay_signup_tab_until';
    var tabWrap = document.querySelector('[data-signup-tab]');
    if (tabWrap) {
      try {
        var tabUntil = parseInt(localStorage.getItem(TAB_KEY) || '0', 10);
        if (tabUntil && Date.now() < tabUntil && window.location.search.indexOf('signup=show') === -1) tabWrap.style.display = 'none';
      } catch (e) {}
      var tabClose = tabWrap.querySelector('[data-signup-tab-close]');
      if (tabClose) tabClose.addEventListener('click', function (e) {
        e.stopPropagation();
        tabWrap.style.display = 'none';
        try { localStorage.setItem(TAB_KEY, String(Date.now() + days * 86400000)); } catch (err) {}
      });
    }
    modal.querySelectorAll('[data-signup-close]').forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
    /* Signup posts in the BACKGROUND (fetch), then the intro swaps for the
       "Here's your code" panel — no full-page redirect, so no jump-scroll to
       the newsletter section. The subscriber still lands in admin → Customers
       (tagged newsletter,signup-modal) because it's the same customer form. */
    var form = modal.querySelector('form');
    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      remember();
      var btn = form.querySelector('button[type="submit"]');
      if (btn) { btn.disabled = true; btn.textContent = 'One moment…'; }
      fetch(form.getAttribute('action') || '/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(new FormData(form)).toString()
      }).catch(function () { /* subscribe is fire-and-forget; the code still shows */ })
        .then(function () {
          var intro = modal.querySelector('[data-signup-intro]');
          var done = modal.querySelector('[data-signup-done]');
          if (intro) intro.hidden = true;
          if (done) done.hidden = false;
        });
    });
    var copyBtn = modal.querySelector('[data-signup-copy]');
    var codeEl = modal.querySelector('[data-signup-code]');
    function copyCode() {
      var code = codeEl ? codeEl.textContent.trim() : '';
      function done() {
        if (copyBtn) {
          copyBtn.textContent = 'Copied ✓';
          setTimeout(function () { copyBtn.textContent = 'Copy'; }, 1800);
        }
      }
      function fallback() {
        // No async clipboard (older Safari/http): select the code so a manual
        // copy is one keystroke, and try the legacy command.
        var r = document.createRange(); r.selectNodeContents(codeEl);
        var s = window.getSelection(); s.removeAllRanges(); s.addRange(r);
        try { if (document.execCommand('copy')) done(); } catch (err) {}
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done, fallback);
      } else { fallback(); }
    }
    if (copyBtn) copyBtn.addEventListener('click', copyCode);
    // The chip itself is also tap-to-copy — bigger target on mobile.
    if (codeEl) codeEl.addEventListener('click', copyCode);
    /* Post-redirect fallback: if a stale-cached (or JS-less) visitor submitted
       the old way, the form's return_to lands back here with ?glow_signup=1 —
       reopen straight onto the code panel instead of Shopify's default
       #contact_form anchor jump. */
    if (window.location.search.indexOf('glow_signup=1') !== -1) {
      var introEl = modal.querySelector('[data-signup-intro]');
      var doneEl = modal.querySelector('[data-signup-done]');
      if (introEl) introEl.hidden = true;
      if (doneEl) doneEl.hidden = false;
      open();
      return;
    }
    // Auto-show is what the dismissal gates.
    var force = window.location.search.indexOf('signup=show') !== -1;
    if (!force) {
      try {
        var until = parseInt(localStorage.getItem(KEY) || '0', 10);
        if (until && Date.now() < until) return;
      } catch (e) { /* private mode — just show it */ }
    }
    setTimeout(open, (parseFloat(modal.getAttribute('data-delay') || '2')) * 1000);
  }

  /* ---- Shop grid: filter rail (shop-grid section) ----
     Rail items with [data-shop-filter] filter the grid in place:
     'all' shows everything; any other tag is matched against each
     .shop-card's data-cats. Promo tiles only show on 'all'. */
  function initShopFilter() {
    var links = document.querySelectorAll('[data-shop-filter]');
    if (!links.length) return;
    links.forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        var f = (a.getAttribute('data-shop-filter') || 'all').toLowerCase();
        document.querySelectorAll('.shop__filter-item').forEach(function (li) { li.classList.remove('is-active'); });
        var li = a.closest('.shop__filter-item');
        if (li) li.classList.add('is-active');
        document.querySelectorAll('.shop__grid .shop-card').forEach(function (card) {
          var cats = (card.getAttribute('data-cats') || '').toLowerCase();
          card.style.display = (f === 'all' || cats.indexOf(f) !== -1) ? '' : 'none';
        });
        document.querySelectorAll('.shop__grid .shop-tile').forEach(function (tile) {
          tile.style.display = (f === 'all') ? '' : 'none';
        });
      });
    });
  }

  /* ---- Collection: filter drawer, sort, view switcher, load more ---- */
  function initCollection() {
    var filterBtn = document.querySelector('[data-filter-toggle]');
    var sidebar = document.querySelector('[data-filter-sidebar]');
    if (filterBtn && sidebar) {
      filterBtn.addEventListener('click', function () {
        var open = sidebar.classList.toggle('is-open');
        filterBtn.classList.toggle('is-open', open);
      });
      var closeBtn = sidebar.querySelector('[data-filter-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () {
        sidebar.classList.remove('is-open');
        filterBtn.classList.remove('is-open');
      });
    }

    var sortWrap = document.querySelector('[data-sortby]');
    if (sortWrap) {
      var trigger = sortWrap.querySelector('[data-sortby-trigger]');
      if (trigger) trigger.addEventListener('click', function () { sortWrap.classList.toggle('is-open'); });
      sortWrap.querySelectorAll('[data-sortby-item]').forEach(function (item) {
        item.addEventListener('click', function () {
          var url = new URL(window.location.href);
          url.searchParams.set('sort_by', item.dataset.sortbyItem);
          window.location.href = url.toString();
        });
      });
      document.addEventListener('click', function (e) {
        if (!sortWrap.contains(e.target)) sortWrap.classList.remove('is-open');
      });
    }

    var grid = document.getElementById('product-grid');
    document.querySelectorAll('[data-grid-view]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (!grid) return;
        document.querySelectorAll('[data-grid-view]').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        grid.dataset.grid = btn.dataset.gridView;
      });
    });

    var loadBtn = document.querySelector('[data-load-more]');
    if (loadBtn && grid) {
      loadBtn.addEventListener('click', function () {
        var next = grid.dataset.nextUrl;
        if (!next) return;
        loadBtn.classList.add('loading');
        fetch(next)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var newGrid = doc.getElementById('product-grid');
            if (newGrid) {
              Array.prototype.forEach.call(newGrid.children, function (child) {
                grid.appendChild(child.cloneNode(true));
              });
              grid.dataset.nextUrl = newGrid.dataset.nextUrl || '';
              if (!newGrid.dataset.nextUrl) loadBtn.style.display = 'none';
            }
            initImageSettle();
            // Count only product tiles — an "empty" paragraph can also live here.
            var shown = grid.querySelectorAll('.product--item').length;
            var countEl = document.querySelector('[data-shown-count]');
            var bar = document.querySelector('[data-progress-bar]');
            if (countEl) countEl.textContent = shown;
            if (bar) {
              var max = parseInt(bar.dataset.max, 10) || shown;
              bar.style.width = Math.min(100, (shown / max) * 100) + '%';
            }
          })
          .catch(function () {})
          .then(function () { loadBtn.classList.remove('loading'); });
      });
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    initReveals();
    initImageSettle();
    initDrawers();
    initQuickAdd();
    initCartDrawer();
    initIngredientTabs();
    initPdpTabs();
    initPdpIngredients();
    document.querySelectorAll('[data-carousel]').forEach(initCarousel);
    initProductGallery();
    initQuantity();
    initVariants();
    initTabs();
    initModals();
    initFaq();
    initShopFilter();
    initSignupModal();
    initPdpPlans();
    initWhyMattersHeight();
    initCollection();
    initPageTransition();
  });

  /* ---- Minimal page-to-page transition ----
     .main-content fades/rises in via CSS on every load; on same-origin link
     clicks we fade the body out first, then navigate. Respects
     prefers-reduced-motion and never hijacks new-tab/download/hash/modified
     clicks. bfcache-safe via pageshow. */
  function initPageTransition() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Clear the leaving state if the page is restored from bfcache (back button)
    window.addEventListener('pageshow', function () { document.body.classList.remove('is-leaving'); });

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest('a');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#' || a.target === '_blank' || a.hasAttribute('download')) return;
      if (a.getAttribute('rel') === 'external' || /^(mailto:|tel:|javascript:)/i.test(href)) return;
      var url;
      try { url = new URL(a.href, location.href); } catch (err) { return; }
      if (url.origin !== location.origin) return;                 // external site
      if (url.pathname === location.pathname && url.hash) return; // same-page anchor
      e.preventDefault();
      document.body.classList.add('is-leaving');
      setTimeout(function () { window.location.href = a.href; }, 160);
    });
  }
})();
